<script>
import messages from '@/mixins/messages'
import { timeDateFormattedPretty } from '@/util'
import ReservationCheckoutResource from '../resources/reservationCheckout'
import PaymentMethod from './PaymentMethod.vue'
import TermsOfServiceLink from './TermsOfServiceLink.vue'
import ApexClubPitch from './ApexClubPitch.vue'
import ApexClubDetail from './ApexClubDetail.vue'
import RelatedImage from './RelatedImage.vue'
import VoucherResource from '../resources/voucher'

export default {
    name: 'ReservationBookMulti',
    components: {
        PaymentMethod,
        TermsOfServiceLink,
        RelatedImage,
        ApexClubPitch,
        ApexClubDetail,
    },
    computed: {
        hasPaymentMethod() {
            return Object.keys(this.userCardDetails).length > 0
        },
        isApexClubMember() {
            return this.userResource.attributes.credits_subscription_in_good_standing
        },
        bookItButtonDisabled() {
            return this.creatingReservation
        },
        shouldShowReservationBooking() {
            return this.hasPaymentMethod || this.skipPayment
        },
        suggestedSize() {
            return 'md'
        },
        locationCurrencySymbol() {
            return this.checkoutData.currency_symbol || '$'
        },
        quoteReservations() {
            return [...this.reservationResources, ...this.addedReservations]
        },
        upsellRule() {
            return this.checkoutData.pricing_rule_upsell ? this.checkoutData.pricing_rule_upsell.rules[0] : null
        },
        amountUserWillSave() {
            if (this.checkoutData.credits_program_saves) {
                return this.locationCurrencySymbol + this.moneyFormat(this.checkoutData.credits_program_saves)
            }

            return '20%'
        },
        paymentLinkEnabled() {
            if (this.usePaymentLink) {
                return this.checkoutData.total_cents > 0
            }

            return false
        }
    },
    mixins: [messages],
    props: {
        reservationResources: {
            type: Array,
            required: true
        },
        usePaymentLink: Boolean,
        packageDeal: {
            type: Object,
            default: null
        }
    },
    watch: {
        shouldShowReservationBooking() {
            this.$emit('size', this.suggestedSize)
        }
    },
    data() {
        return {
            loading: true,
            selectedCalendarBecomesUnavailable: false,
            creatingReservation: false,
            userResource: null,
            calendarData: {},
            checkoutData: {},
            userCardDetails: {},
            skipPayment: false,
            voucherId: '',
            voucherResource: null,
            voucherEntryVisible: false,
            addedReservations: [],
            addingUpsell: false,
        }
    },
    methods: {
        timeDateFormattedPretty,
        async load() {
            this.userResource = await this.$store.dispatch('getUserResource')
            this.userCardDetails = await this.userResource.getCard()
            return await Promise.all(this.reservationResources.map((reserv) => reserv.resolveRelated()))
        },
        onCardAdded(result) {
            this.userCardDetails = result.card
        },
        async onApexClubRegister(credits) {
            this.$refs.apexClubModal.hide()
            this.userResource.attributes.credits_balance = credits
            this.userResource.attributes.credits_subscription_in_good_standing = true
            await this.updateTotalCost()
        },
        async updateTotalCost() {
            const voucherId = this.voucherResource ? this.voucherResource.id : null
            const packageDealId = this.packageDeal ? this.packageDeal.id : null
            try {
                this.checkoutData = await ReservationCheckoutResource.quote(this.quoteReservations, voucherId, packageDealId)
            } catch (e) {
                const status = e.response?.status
                if (status > 399 && status < 500) {
                    this.$emit('error', this.getResponseMessageFromAxiosException(e))
                } else {
                    throw e
                }
            }
        },
        pricingRuleAmount(rule) {
            return rule.adjustment_type === 'PERCENTAGE' ? `${rule.value}%` : `${this.locationCurrencySymbol}${this.moneyFormat(rule.value)}`
        },
        async addUpsellSessions() {
            this.addingUpsell = true
            try {
                const ReservationResource = require('@/resources/reservation').default
                const additions = this.checkoutData.pricing_rule_upsell.reservations.map((reserv) => new ReservationResource({ calendar: reserv.calendar, slot: reserv.slot }))
                this.addedReservations = [...this.addedReservations, ...additions]
                await this.updateTotalCost()
            } finally {
                this.addingUpsell = false
            }
        },
        async addVoucher() {
            try {
                this.voucherResource = await VoucherResource.detail(this.voucherId.toUpperCase())
                await this.updateTotalCost()
            } catch(e) {
                if(e.response?.status > 399 && e.response?.status < 500) {
                    this.addMessage('warning', 'The discount code you entered was not found or has already been used')
                    await this.removeVoucher()
                } else {
                    throw e
                }
            }
        },
        async removeVoucher() {
            this.voucherId = ''
            this.voucherResource = null
            await this.updateTotalCost()
        },
        async paymentLinkRedirect() {
            const { checkout_id } = this.checkoutData
            const checkout = await ReservationCheckoutResource.detail(checkout_id)
            const url = await checkout.createPaymentLink()
            window.location.href = url
        },
        async createReservation() {
            try {
                this.creatingReservation = true
                const value = this.checkoutData.total_cents / 100
                const commitData = await this.checkoutData.commit()
                await this.$store.dispatch('getUserResource')
                this.$analytics.track('checkout_success', { value, method: 'checkout', currency: 'USD' })
                this.$emit('success', commitData)
            } catch(e) {
                const failMessage = this.getResponseMessageFromAxiosException(e)
                this.$analytics.track('exception_during_booking', { message: e.message, detail: failMessage })
                this.$emit('error', failMessage)
            } finally {
                this.creatingReservation = false
            }
        }
    },
    async mounted() {
        this.loading = true
        await this.load()

        if (this.$store.getters.locationSelected?.attributes?.checkout_method !== 'CAPTURE') {
            this.skipPayment = true
        }
        
        await this.updateTotalCost()
        this.$emit('size', this.suggestedSize)
        this.loading = false
    },
}
</script>

<template>
    <div class="reservation-book-body">
        <b-row v-if="!loading">
            <b-col>
                <b-row>
                    <b-col>
                        <b-alert variant="primary" show v-if="upsellRule">
                            <b-icon icon="tag-fill" /> Add 1 more session<span v-if="checkoutData.pricing_rule_upsell.reservations.length > 1"> on {{ checkoutData.pricing_rule_upsell.reservations.length }} simulators</span> for {{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.pricing_rule_upsell.additional_cents) }} more and get <strong>{{ upsellRule.name }}</strong>: {{ pricingRuleAmount(upsellRule) }} off per session (save {{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.pricing_rule_upsell.savings_cents) }}).
                            <b-button variant="primary" size="sm" class="d-block mt-2" :disabled="addingUpsell" @click="addUpsellSessions">Add session</b-button>
                        </b-alert>
                        <b-table-simple>
                            <b-thead>
                                <b-tr>
                                    <b-th>Simulator</b-th>
                                    <b-th class="text-right">Each</b-th>
                                    <b-th>Qty</b-th>
                                    <b-th class="text-right">Cost</b-th>
                                </b-tr>
                            </b-thead>

                            <b-tbody v-for="(calendarItem, i) in checkoutData.line_items" :key="i">
                                <b-tr>
                                    <b-td class="pb-0"><strong v-b-tooltip.hover :title="calendarItem.calendar_type_name">{{ String(calendarItem.calendar_name) }}</strong></b-td>
                                    <b-td class="pb-0 text-right">{{ locationCurrencySymbol }}{{ moneyFormat(calendarItem.calendar_cost_cents) }}</b-td>
                                    <b-td class="pb-0">{{ calendarItem.calendar_qty }}</b-td>
                                    <b-td class="pb-0 text-right">{{ locationCurrencySymbol }}{{ moneyFormat(calendarItem.calendar_total_cents) }}</b-td>
                                </b-tr>
                                <b-tr>
                                    <b-td colspan="4" class="pt-0 border-0 text-muted">
                                        <small>{{ timeDateFormattedPretty(calendarItem.time, { short: true }) }} <span v-if="true">({{ calendarItem.calendar_location }})</span></small>
                                    </b-td>
                                </b-tr>

                                <b-tr v-if="calendarItem.calendar_booking_fee_cents > 0">
                                    <b-td class="pt-0 border-0 text-muted">
                                        <small>Booking Fee <span class="ml-1"><a href="javascript:{}" @click="$refs.bookingFeeModal.show()"><b-icon icon="question-circle" /></a></span></small>
                                    </b-td>
                                    <b-td class="pt-0 border-0 text-muted text-right">
                                        <small>{{ locationCurrencySymbol }}{{ moneyFormat(calendarItem.calendar_booking_fee_cents) }}</small>
                                    </b-td>
                                    <b-td class="pt-0 border-0 text-muted">
                                        <small>1</small>
                                    </b-td>
                                    <b-td class="pt-0 border-0 text-muted text-right">
                                        <small>{{ locationCurrencySymbol }}{{ moneyFormat(calendarItem.calendar_booking_fee_cents) }}</small>
                                    </b-td>
                                </b-tr>
                            </b-tbody>


                            <b-tbody>
                                <b-tr>
                                    <b-td colspan="3"><strong>Subtotal</strong></b-td>
                                    <b-td class="text-right">{{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.subtotal_cents) }}</b-td>
                                </b-tr>
                                <b-tr v-if="checkoutData.pricing_rules_applied && checkoutData.pricing_rules_applied.length > 0">
                                    <b-td colspan="4" class="text-right">
                                        <small v-for="(rule, i) in checkoutData.pricing_rules_applied" :key="i" class="text-success d-block">
                                            <b-icon icon="coin" /> <strong>{{ rule.name }}</strong> applied (saved {{ locationCurrencySymbol }}{{ moneyFormat(rule.savings_cents) }})
                                        </small>
                                    </b-td>
                                </b-tr>
                                <b-tr v-if="checkoutData.package_deal_applied && checkoutData.package_deal_applied.length > 0">
                                    <b-td colspan="4" class="text-right">
                                        <small v-for="(deal, i) in checkoutData.package_deal_applied" :key="i" class="text-success d-block">
                                            <b-icon icon="tag-fill" /> <strong>{{ deal.name }}</strong> package deal<span v-if="deal.savings_cents"> (saved {{ locationCurrencySymbol }}{{ moneyFormat(deal.savings_cents) }})</span>
                                        </small>
                                    </b-td>
                                </b-tr>
                                <b-tr v-if="voucherEntryVisible">
                                    <b-td colspan="4" class="text-right">
                                        <small v-if="this.voucherResource" class="text-success"><b-icon icon="coin" /> <strong>{{ this.voucherResource }}</strong> applied (saved {{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.discount_cents) }}) — <a href="javascript:{}" @click="removeVoucher">remove</a></small>
                                        <b-input-group v-else>
                                            <b-form-input v-model="voucherId" size="sm" class="col-md-8 offset-md-3 text-uppercase"></b-form-input>
                                            <b-input-group-append>
                                                <b-button variant="outline-primary" size="sm" @click="addVoucher" :disabled="!voucherId">Apply Discount</b-button>
                                            </b-input-group-append>
                                        </b-input-group>
                                    </b-td>
                                </b-tr>
                                <b-tr v-if="this.voucherResource">
                                    <b-td colspan="3"><strong>Subtotal after Discounts</strong></b-td>
                                    <b-td class="text-right">{{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.pre_tax_cents) }}</b-td>
                                </b-tr>
                                <b-tr v-if="checkoutData.sales_tax_cents > 0">
                                    <b-td colspan="3"><strong>Sales Tax</strong></b-td>
                                    <b-td class="text-right">{{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.sales_tax_cents) }}</b-td>
                                </b-tr>
                            </b-tbody>

                            <b-tbody v-if="$appConfigBool('APEX_CLUB_ENABLED', true)">
                                <b-tr>
                                    <b-td class="text-nowrap"><strong>{{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</strong> <a href="javascript:{}" v-if="!isApexClubMember" @click="$refs.apexClubModal.show()"><b-icon icon="question-circle" /></a></b-td>
                                    <b-td colspan="3" class="text-right text-nowrap">
                                        <small v-if="isApexClubMember">
                                            <strong><b-avatar icon="currency-exchange" size="1em"></b-avatar> {{ userResource.attributes.credits_balance }}</strong> {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} Credits Remaining
                                        </small>
                                        <small v-else>
                                            <span class="text-muted mr-3"><a href="javascript:{}" @click="$refs.apexClubModal.show()">Save {{ amountUserWillSave }} Now</a> <b-icon icon="arrow-right" animation="cylon" /></span>
                                            <a href="javascript:{}" @click="$refs.apexClubModal.show()">Sign Up</a>
                                        </small>
                                    </b-td>
                                </b-tr>
                                <b-tr v-if="isApexClubMember && checkoutData.credits_using > 0">
                                    <b-td><strong>Credits Redeeming</strong></b-td>
                                    <b-td colspan="3" class="text-right text-success"><strong>{{ checkoutData.credits_using }} (worth {{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.credits_value_cents) }})</strong></b-td>
                                </b-tr>
                            </b-tbody>
                            
                            <b-tbody>
                                <b-tr>
                                    <b-td colspan="2"><small v-if="!voucherEntryVisible"><a href="javascript:{}" @click="voucherEntryVisible = !voucherEntryVisible">Have a discount code?</a></small></b-td>
                                    <b-td><strong>Total</strong></b-td>
                                    <b-td class="text-right"><strong>{{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.total_cents) }}</strong></b-td>
                                </b-tr>
                            </b-tbody>
                        </b-table-simple>

                    </b-col>
                </b-row>

                <b-row v-if="!paymentLinkEnabled">
                    <b-col>
                        <messages />
                        <b-alert variant="info" :show="hasPaymentMethod && checkoutData.total_cents > 0 && checkoutData.method == 'CAPTURE'">Your <strong>{{ userCardDetails.brand }} ending in {{ userCardDetails.last4 }}</strong> will be charged <strong>{{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.total_cents) }}</strong></b-alert>
                        <payment-method v-if="!skipPayment && !hasPaymentMethod" cols-lg="12" :user-resource="userResource" @success="onCardAdded">
                            <b-alert variant="success" show><b-icon icon="lock-fill" /> Your card will not be charged yet</b-alert>
                        </payment-method>
                        <b-alert variant="danger" :show="selectedCalendarBecomesUnavailable"><b-icon icon="exclamation-triangle" /> Sorry, one of the simulators you selected is no longer available</b-alert>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <b-button v-if="paymentLinkEnabled" variant="primary" class="mt-3" @click="paymentLinkRedirect()">Continue Checkout</b-button>
                        <div v-else>
                            <small v-if="skipPayment || hasPaymentMethod" class="text-muted"><terms-of-service-link /></small>
                            <b-button v-if="shouldShowReservationBooking" variant="primary" class="mt-3" @click="createReservation" :disabled="bookItButtonDisabled">{{ Object.keys(checkoutData.line_items || {}).length > 1 ? `Reserve ${Object.keys(checkoutData.line_items || {}).length} Simulators!` : 'Reserve Simulators' }}</b-button>
                        </div>
                    </b-col>
                </b-row>

            </b-col>
        </b-row>

        <b-row v-else>
            <b-col>
                <b-skeleton :width="Math.floor(Math.random() * (100 - 60 + 1) + 60) + '%'" v-for="i in 10" :key="i" />
            </b-col>
        </b-row>

        <b-modal ref="apexClubModal" :title="$appConfig('APEX_CLUB_NAME', 'Apex Club') + ' Enrollment'" size="md" hide-footer no-fade centered>
            <apex-club-pitch @success="onApexClubRegister" >
                <p class="text-muted"><small>(Yes, you can use your sessions for friends and family!) — <a href="javascript:{}" v-b-toggle.collapse-faq>More Info &amp; FAQ</a></small></p>
                <b-collapse id="collapse-faq" class="mb-3">
                    <b-card>
                        <apex-club-detail />
                    </b-card>
                </b-collapse>
            </apex-club-pitch>
        </b-modal>

        <b-modal ref="bookingFeeModal" title="Booking Fees" size="md" ok-only no-fade centered>
            <p class="lead font-weight-bold">Why Do We Charge a Booking Fee?</p>
            <p>Our goal is to provide the best possible experience for all our racers. The <strong>per-simulator booking fee</strong> helps us achieve that in several ways:</p>
            <p class="lead font-weight-bold">Encouraging Longer Sessions</p>
            <p>Getting a racer set up in a simulator takes time. By promoting longer bookings, we reduce downtime and ensure you spend more time racing and less time setting up.</p>
            <p class="lead font-weight-bold">Maximizing Availability</p>
            <p>Since we allow sharing, you can book fewer simulators for a longer time to avoid these fees. This makes it easier for groups to enjoy their sessions without unnecessary costs.</p>
            <p class="lead font-weight-bold">Supporting a High-Quality Experience</p>
            <p>The fee helps us maintain and improve our equipment, ensuring that every session meets the highest standards.</p>
            <p class="lead font-weight-bold">Want to Skip the Fees?</p>
            <p>
                <strong>Any booking of {{ checkoutData.booking_fees_threshold * 30 }} minutes or more has booking fees waived!</strong>
                <span v-if="!loading && $appConfigBool('APEX_CLUB_ENABLED', true) && !isApexClubMember">
                    You can also <a href="javascript:{}" @click="$refs.apexClubModal.show()"><strong>Join our Apex Club!</strong></a> Members get these <strong>fees waived entirely</strong>, along with <strong>saving over 20%</strong> on reservations and other exclusive perks.
                </span>
            </p>
            <p>We appreciate your support and look forward to seeing you on the track!</p>
        </b-modal>
    </div>
</template>

<style lang="scss">
.reservation-book-body {
    img {
        max-width: 100%;
    }
    
    td.more-info {
        font-size: 0.875em;
        border: none;
    }
}
</style>
